import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { space } from "@tokens/index";
import { Button, ConfirmDialog, SkeletonRows, Toast } from "@primitives/index";
import { QueueDetail, QueueTimelineDialog } from "@compositions/index";
import { AdminPage, ErrorAlert } from "../admin-ui";
import type { QueueMember, UserSearchResult } from "../queues";
import {
    useAddQueueMember,
    useDeactivateQueue,
    useQueue,
    useQueueNotificationStatus,
    useQueueTimeline,
    useReactivateQueue,
    useRemoveQueueMember,
    useRenameQueue,
    useUserSearch,
} from "../queues";
import { resolveUserDisplayName } from "../management-groups-admin/userDisplayName";
import { QueueRoutingRulesSection } from "../QueueRoutingRulesSection";
import { describeQueueLoadFailure } from "./helpers";
import { QueueOwnerPanel } from "./QueueOwnerPanel";
import { QueueWorkItemsSection } from "./QueueWorkItemsSection";

interface QueueDetailRouteProps {
    readonly id: string;
    readonly onBackToList: () => void;
}

function BackToQueues({ onClick }: { readonly onClick: () => void }): ReactElement {
    return (
        <Button variant="ghost" size="sm" onClick={onClick}>
            <ArrowLeft size={14} strokeWidth={2.25} aria-hidden="true" />
            All queues
        </Button>
    );
}

function QueueDetailRoute({ id, onBackToList }: QueueDetailRouteProps): ReactElement {
    const { data: queue, isLoading, error } = useQueue(id);
    const { mutedUserIds } = useQueueNotificationStatus(id);
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
    // The member removed last, offered back through an undo toast.
    const [removedMember, setRemovedMember] = useState<QueueMember | null>(null);
    const timelineQuery = useQueueTimeline(id, timelineOpen);

    const renameMutation = useRenameQueue();
    const addMemberMutation = useAddQueueMember();
    const removeMemberMutation = useRemoveQueueMember();
    const deactivateMutation = useDeactivateQueue();
    const reactivateMutation = useReactivateQueue();

    const [searchTerm, setSearchTerm] = useState("");
    const userSearch = useUserSearch({ q: searchTerm });

    // Track which member is currently being removed so the row can render a
    // disabled state. Mutation `variables` only carries the userId of the
    // in-flight call; null means "no removal in flight."
    const removingUserId = removeMemberMutation.isPending
        ? (removeMemberMutation.variables?.userId ?? null)
        : null;

    // The API resolves membership, owning-management-group administration,
    // and global break-glass into one caller-relative capability. Keeping the
    // authority decision server-side prevents UI claim parsing from drifting
    // away from the endpoint checks.
    const canAdminister = queue !== undefined && queue.isWorkable && queue.canAdminister;
    const canManageLifecycle = canAdminister && queue !== undefined && !queue.isPersonal;
    const canEdit = canAdminister && queue?.status === "active";

    const handleRename = useCallback(
        (name: string) => {
            renameMutation.mutate({ id, name });
        },
        [renameMutation, id],
    );

    const handleAdd = useCallback(
        (user: UserSearchResult) => {
            addMemberMutation.mutate({
                queueId: id,
                userId: user.id,
                displayName: resolveUserDisplayName(user),
            });
            setSearchTerm("");
        },
        [addMemberMutation, id],
    );

    // Removal is immediate (the cache drops the row optimistically) and
    // reversible for a few seconds through the toast.
    const handleRemove = useCallback(
        (member: QueueMember) => {
            setRemovedMember(null);
            removeMemberMutation.mutate(
                { queueId: id, userId: member.userId },
                { onSuccess: () => setRemovedMember(member) },
            );
        },
        [removeMemberMutation, id],
    );

    const undoRemove = useCallback(() => {
        if (removedMember === null) return;
        addMemberMutation.mutate({
            queueId: id,
            userId: removedMember.userId,
            displayName: removedMember.displayName,
        });
        setRemovedMember(null);
    }, [addMemberMutation, id, removedMember]);

    const dismissUndo = useCallback(() => setRemovedMember(null), []);
    const openTimeline = useCallback(() => setTimelineOpen(true), []);

    const runDeactivate = useCallback(() => {
        if (queue === undefined) return;
        reactivateMutation.reset();
        deactivateMutation.mutate(queue.id);
    }, [queue, deactivateMutation, reactivateMutation]);

    // A retry while the queue is already deactivating needs no confirmation;
    // the first transition out of `active` does.
    const handleDeactivate = useCallback(() => {
        if (queue === undefined) return;
        if (queue.status === "deactivating") {
            runDeactivate();
            return;
        }
        setConfirmingDeactivate(true);
    }, [queue, runDeactivate]);

    const confirmDeactivate = useCallback(() => {
        setConfirmingDeactivate(false);
        runDeactivate();
    }, [runDeactivate]);

    const handleReactivate = useCallback(() => {
        if (queue === undefined) return;
        deactivateMutation.reset();
        reactivateMutation.mutate(queue.id);
    }, [queue, deactivateMutation, reactivateMutation]);

    if (error !== null) {
        return (
            <div style={{ display: "grid", gap: space[4] }}>
                <div>
                    <BackToQueues onClick={onBackToList} />
                </div>
                <ErrorAlert>{describeQueueLoadFailure(error)}</ErrorAlert>
            </div>
        );
    }

    if (isLoading || queue === undefined) {
        return (
            <div style={{ display: "grid", gap: space[4] }}>
                <div>
                    <BackToQueues onClick={onBackToList} />
                </div>
                <SkeletonRows rows={3} rowHeight={120} shape="card" label="Loading queue" />
            </div>
        );
    }

    const searchResults = userSearch.data?.results ?? [];

    return (
        <div style={{ display: "grid", gap: space[5], minWidth: 0 }}>
            <div>
                <BackToQueues onClick={onBackToList} />
            </div>
            <QueueDetail
                queue={queue}
                canEdit={canEdit}
                canManageLifecycle={canManageLifecycle}
                mutedUserIds={mutedUserIds}
                searchTerm={searchTerm}
                searchResults={searchResults}
                isSearching={userSearch.isFetching}
                searchError={userSearch.error}
                isRenaming={renameMutation.isPending}
                renameError={renameMutation.error}
                isAddingMember={addMemberMutation.isPending}
                addMemberError={addMemberMutation.error}
                removingUserId={removingUserId}
                removeMemberError={removeMemberMutation.error}
                isChangingStatus={deactivateMutation.isPending || reactivateMutation.isPending}
                lifecycleError={deactivateMutation.error ?? reactivateMutation.error}
                onRename={handleRename}
                onSearchTermChange={setSearchTerm}
                onAddMember={handleAdd}
                onRemoveMember={handleRemove}
                onDeactivate={handleDeactivate}
                onReactivate={handleReactivate}
                onOpenTimeline={openTimeline}
            />
            {!queue.isPersonal && queue.isWorkable && (
                <QueueOwnerPanel queue={queue} canEdit={canEdit} />
            )}
            {queue.isWorkable && <QueueWorkItemsSection queueId={queue.id} />}
            {queue.isWorkable && !queue.isPersonal && (
                <QueueRoutingRulesSection queueId={queue.id} canEdit={canEdit} />
            )}
            <QueueTimelineDialog
                isOpen={timelineOpen}
                queueName={queue.name}
                events={timelineQuery.data?.events ?? []}
                // `isPending` (no data yet) rather than `isLoading` (pending
                // AND fetching): the query is disabled until the dialog
                // opens, so `isLoading` is false on the first frame and the
                // empty state would flash before the skeleton.
                isLoading={timelineQuery.isPending}
                error={timelineQuery.error as Error | null}
                truncated={timelineQuery.data?.truncated ?? false}
                partial={timelineQuery.data?.partial ?? false}
                onClose={() => setTimelineOpen(false)}
            />
            <ConfirmDialog
                open={confirmingDeactivate}
                title="Deactivate queue"
                message={`Deactivate "${queue.name}"? The queue will become read-only and stop accepting new operational work. You can reactivate it later.`}
                confirmLabel="Deactivate"
                tone="danger"
                onConfirm={confirmDeactivate}
                onCancel={() => setConfirmingDeactivate(false)}
            />
            <Toast
                open={removedMember !== null}
                message={removedMember !== null ? `Removed ${removedMember.displayName}` : ""}
                action={{ label: "Undo", onClick: undoRemove }}
                onDismiss={dismissUndo}
                autoHideMs={6000}
                testId="member-removed-toast"
            />
        </div>
    );
}

export function QueueDetailPage(): ReactElement {
    const navigate = useNavigate();
    const params = useParams<{ id: string }>();
    const handleBackToList = useCallback(() => void navigate("/queues"), [navigate]);
    const id = params.id ?? "";

    // Defensive redirect — direct navigation without an id is meaningless.
    useEffect(() => {
        if (id.length === 0) {
            void navigate("/queues", { replace: true });
        }
    }, [id, navigate]);

    if (id.length === 0) return <></>;

    return (
        <AdminPage>
            <QueueDetailRoute id={id} onBackToList={handleBackToList} />
        </AdminPage>
    );
}
